import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { AdminTabs, ComparablesTabs, MainProjectTabs, ModulesEnum, ProjectValuationTabs, RaciTabs } from './enum';
import { AppServiceHelperService } from '../services/app-service-helper.service';

/**
 * Navigation service
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationService {
  /**
   * The active module observable
   */
  activeModule$: Observable<ModulesEnum>;

  /**
   * The active project valuation tab observable
   */
  activeProjectValuationTab$: Observable<ProjectValuationTabs>;

  /**
   * The active comparables tab observable
   */
  activeComparablesTab$: Observable<ComparablesTabs>;

  /**
   * The active project main tab observable
   */
  activeMainProjectTab$: Observable<MainProjectTabs>;

  /**
   * The active admin tab observable
   */
  activeAdminTab$: Observable<AdminTabs>;

  /**
   * The active raci tab observable
   */
  activeRaciTab$: Observable<RaciTabs>;

  /**
  * The active valuation event properties grouping Observable
  */
  activeValuationEventPropertiesGrouping$: Observable<number>;

  /**
  * The active valuation event properties grouping Observable
  */
  activeValuationEventParticipantsGrouping$: Observable<number>;

  /**
* The active valuation event currencies grouping Observable
*/
  activeValuationEventCurrenciesGrouping$: Observable<number>;

  /**
* The pending tasks count Observable
*/
  pendingTasksCount$: Observable<number>;

  /**
   * The active admin tab
   */
  private activeAdminTab: AdminTabs = AdminTabs.Users;

  /**
  * The active raci tab
  */
  private activeRaciTab: RaciTabs = RaciTabs.Overview;

  /**
   * The active project valuation tab subject
   */
  private _activeProjectValuationTab: BehaviorSubject<number> = new BehaviorSubject(ProjectValuationTabs.Projects);

  /**
   * The active comparables tab subject
   */
  private _activeComparablesTab: BehaviorSubject<number> = new BehaviorSubject(ComparablesTabs.Properties);

  /**
   * The active project main tab subject
   */
  private _activeMainProjectsTab: BehaviorSubject<number> = new BehaviorSubject(MainProjectTabs.Projects);

  /**
   * The active admin tab subject
   */
  private _activeAdminTab: BehaviorSubject<number> = new BehaviorSubject(AdminTabs.Users);

  /**
   * The active raci tab subject
   */
  private _activeRaciTab: BehaviorSubject<number> = new BehaviorSubject(RaciTabs.Overview);

  /**
   * The active module subject
   */
  private _activeModule: BehaviorSubject<number> = new BehaviorSubject(ModulesEnum.ProjectValuation);

  /**
   * The active valuation event properties grouping
   */
  private _activeValuationEventPropertiesGrouping: BehaviorSubject<number> = new BehaviorSubject(0);

  /**
  * The active valuation event properties grouping
  */
  private _activeValuationEventParticipantsGrouping: BehaviorSubject<number> = new BehaviorSubject(0);

  /**
 * The active valuation event currencies grouping
 */
  private _activeValuationEventCurrenciesGrouping: BehaviorSubject<number> = new BehaviorSubject(0);

  /**
* The prending tasks count
*/
  private _pendingTasksCount: BehaviorSubject<number> = new BehaviorSubject(0);
  /**
   * Pending tasks
   */
  private _pendingTasks = 0;

  /**
   * Is count of pending tasks prefetched?
   */
  private _pendingTasksCountPrefetched = false;

  /**
   * Constructor
   * @param httpClient
   * @param appServiceHelper
   */
  constructor(private httpClient: HttpClient, private appServiceHelper: AppServiceHelperService) {
    this.activeModule$ = this._activeModule.asObservable();
    this.activeProjectValuationTab$ = this._activeProjectValuationTab.asObservable();
    this.activeComparablesTab$ = this._activeComparablesTab.asObservable();
    this.activeMainProjectTab$ = this._activeMainProjectsTab.asObservable();
    this.activeAdminTab$ = this._activeAdminTab.asObservable();
    this.activeRaciTab$ = this._activeRaciTab.asObservable();
    this.activeValuationEventPropertiesGrouping$ = this._activeValuationEventPropertiesGrouping.asObservable();
    this.activeValuationEventParticipantsGrouping$ = this._activeValuationEventParticipantsGrouping.asObservable();
    this.activeValuationEventCurrenciesGrouping$ = this._activeValuationEventCurrenciesGrouping.asObservable();
    this.pendingTasksCount$ = this._pendingTasksCount.asObservable();
  }
  /**
   * Get pending tasks count
   * @returns
   */
  private getPendingTasksCount() {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.casemanagement.api', `general/tasks/approval/tasks/pending/count`)).pipe(take(1));
  }
  /**
   * Update pending tasks count
   * @param data
   */
  updatePendingTasksCount(data: number) {
    this._pendingTasks = this._pendingTasks - data;
    this._pendingTasksCount.next(this._pendingTasks);
  }

  /**
   * Sets the active module
   * @param module Module
   */
  setActiveModule(module: ModulesEnum, dontLoadPendingTasks = false) {
    if (!this._pendingTasksCountPrefetched && !dontLoadPendingTasks) {
      /*
        moved loading of pending tasks from constructor, so they are not loaded if RD print is in progress
        setActiveModule will be called at least once at the begining of app load, so safe to do so
      */
      this._pendingTasksCountPrefetched = true;
      this.getPendingTasksCount().subscribe(data => {
        this._pendingTasks = data;
        this._pendingTasksCount.next(data);
      });
    }

    this._activeModule.next(module);
  }

  /**
   * Sets the active Project Valuation tab
   * @param tab
   */
  setActiveProjectValuationTab(tab: ProjectValuationTabs) {
    this._activeProjectValuationTab.next(tab);
  }

  /**
   * Set active Main Projects tab
   * @param tab Main Projec tab
   */
  setActiveMainProjectsTab(tab: MainProjectTabs) {
    this._activeMainProjectsTab.next(tab);
  }

  /**
   * Set active Comparables tab
   * @param tab Comparables tab
   */
  setActiveComparablesTab(tab: ComparablesTabs) {
    this._activeComparablesTab.next(tab);
  }

  /**
   * Set Admin active tab
   * @param tab Admin tab
   */
  setActiveAdminTab(tab: AdminTabs) {
    this.activeAdminTab = tab;
    this._activeAdminTab.next(tab);
  }

  /**
   * Sets Raci active tab
   * @param tab Raci Tab
   */
  setActiveRaciTab(tab: RaciTabs) {
    this.activeRaciTab = tab;
    this._activeRaciTab.next(tab);
  }

  /**
   * Emit show raci participants section event
   * @param value Emitted value
   */
  setActiveValuationEventPropertiesGrouping(groupingType: number) {
    this._activeValuationEventPropertiesGrouping.next(groupingType);
  }

  /**
   * Emit show raci participants section event
   * @param value Emitted value
   */
  setActiveValuationEventParticipantsGrouping(value: number) {
    this._activeValuationEventParticipantsGrouping.next(value);
  }

  /**
 * Emit show valuation event currencies
 * @param value Emitted value
 */
  setActiveValuationEventCurrenciesGrouping(groupingType: number) {
    this._activeValuationEventCurrenciesGrouping.next(groupingType);
  }

  /**
   * Retrive active Admin tab
   * */
  getActiveAdminTab(): AdminTabs {
    return this.activeAdminTab;
  }

  /**
   * Get Raci active tab
   */
  getActiveRaciTab(): RaciTabs {
    return this.activeRaciTab;
  }

  /**
   * Check it is Calculation Model Area
   * */
  isCalculationModelArea(): boolean {
    return this.activeAdminTab === AdminTabs.CalculationModel
      || this.activeAdminTab === AdminTabs.CalculationModelEdit
      || this.activeAdminTab === AdminTabs.CalculationModelRegister
      || this.activeAdminTab === AdminTabs.CalculationModelNone;
  }

  /**
   * Check it is Raci Template Area
   * */
  isRaciTemplateArea(): boolean {
    return this.activeAdminTab === AdminTabs.RaciTemplate
      || this.activeAdminTab === AdminTabs.RaciTemplateEdit
      || this.activeAdminTab === AdminTabs.RaciTemplateNew
  }

  /**
   * Are we in the localization area?
   * @returns
   */
  isLocalizationArea(): boolean {
    return this.activeAdminTab === AdminTabs.LocalizationForm || this.activeAdminTab === AdminTabs.LocalizationHost;
  }
}
